import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type KaraokePackage = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  price_cents: number;
  price_unit: string;
  min_guests: number;
  max_guests: number;
  duration_minutes: number;
  includes: string[];
  published: boolean;
  sort_order: number;
};

const COLS =
  "id,name,tagline,description,price_cents,price_unit,min_guests,max_guests,duration_minutes,includes,published,sort_order";

/** Public: published karaoke packages for the booking modal. */
export const listKaraokePackages = createServerFn({ method: "GET" }).handler(
  async (): Promise<KaraokePackage[]> => {
    const supabase = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data, error } = await supabase
      .from("karaoke_packages")
      .select(COLS)
      .eq("published", true)
      .order("sort_order", { ascending: true });
    if (error) return [];
    return (data ?? []) as KaraokePackage[];
  },
);

/** Staff: every package, published or not. */
export const listKaraokePackagesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<KaraokePackage[]> => {
    const { data, error } = await context.supabase
      .from("karaoke_packages")
      .select(COLS)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as KaraokePackage[];
  });

const PackageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(120),
  tagline: z.string().trim().max(200).default(""),
  description: z.string().trim().max(1000).default(""),
  price_cents: z.number().int().min(0).max(100000),
  price_unit: z.enum(["per_person", "per_group"]),
  min_guests: z.number().int().min(1).max(200),
  max_guests: z.number().int().min(1).max(200),
  duration_minutes: z.number().int().min(30).max(360),
  includes: z.array(z.string().trim().max(160)).max(20).default([]),
  published: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(999).default(0),
});

export const upsertKaraokePackage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => PackageSchema.parse(d))
  .handler(async ({ data, context }) => {
    const payload = {
      name: data.name,
      tagline: data.tagline,
      description: data.description,
      price_cents: data.price_cents,
      price_unit: data.price_unit,
      min_guests: data.min_guests,
      max_guests: data.max_guests,
      duration_minutes: data.duration_minutes,
      includes: data.includes,
      published: data.published,
      sort_order: data.sort_order,
    };
    if (data.id) {
      const { error } = await context.supabase
        .from("karaoke_packages")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase
      .from("karaoke_packages")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const deleteKaraokePackage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("karaoke_packages")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Staff: karaoke reservations (upcoming first). */
export const listKaraokeReservations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("reservations")
      .select(
        "id,reference,status,starts_at,duration_minutes,party_size,preferred_area,notes,created_at,guests(full_name,phone,email)",
      )
      .eq("booking_type", "karaoke")
      .order("starts_at", { ascending: true })
      .limit(300);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const setKaraokeReservationStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; status: string }) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["confirmed", "pending_approval", "cancelled", "completed", "no_show"]),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("reservations")
      .update({ status: data.status as never })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    await context.supabase.from("reservation_status_history").insert({
      reservation_id: data.id,
      to_status: data.status as never,
      changed_by: context.userId,
      reason: "Karaoke desk update",
    });
    return { ok: true };
  });
